import { forwardRef, useMemo } from "react";
import { Columns3, Plus, Rows3, Trash2 } from "lucide-react";

const XlsxEditor = forwardRef(function XlsxEditor(
  {
    sheets,
    selectedSheet,
    selectedCell,
    onSelectSheet,
    onSelectCell,
    onChange,
    onAddRow,
    onAddColumn,
    onDeleteRow,
    onDeleteColumn,
  },
  ref,
) {
  const sheet = sheets[selectedSheet];
  const rowCount = sheet?.data?.length || 0;
  const colCount = useMemo(
    () => Math.max(1, ...(sheet?.data?.map((row) => row.length) || [1])),
    [sheet],
  );

  if (!sheet) return <div className="preview-empty">暂无工作表</div>;

  function updateCell(r, c, value) {
    const next = sheets.map((s, si) => {
      if (si !== selectedSheet) return s;
      const data = s.data.map((row) => [...row]);
      while (data.length <= r) data.push([]);
      data[r][c] = value;
      return { ...s, data };
    });
    onChange(next);
  }

  return (
    <div className="xlsx-editor">
      <div className="sheet-tabs" role="tablist">
        {sheets.map((s, i) => (
          <button
            type="button"
            role="tab"
            key={`${s.name}-${i}`}
            className={`sheet-tab ${i === selectedSheet ? "active" : ""}`}
            onClick={() => onSelectSheet(i)}
          >
            {s.name || `Sheet${i + 1}`}
          </button>
        ))}
        <span className="sheet-tabs-note">就地编辑单元格内容</span>
      </div>
      <div className="sheet-toolbar">
        <button type="button" className="tool-btn" title="添加一行" onClick={onAddRow} disabled={!sheet}>
          <Rows3 size={16} />
          <span>添加行</span>
        </button>
        <button type="button" className="tool-btn" title="添加一列" onClick={onAddColumn} disabled={!sheet}>
          <Columns3 size={16} />
          <span>添加列</span>
        </button>
        <button type="button" className="tool-btn" title="删除选中行" onClick={onDeleteRow} disabled={!sheet}>
          <Trash2 size={16} />
          <span>删除行</span>
        </button>
        <button type="button" className="tool-btn" title="删除选中列" onClick={onDeleteColumn} disabled={!sheet}>
          <Trash2 size={16} />
          <span>删除列</span>
        </button>
        <span className="sheet-toolbar-note">共 {rowCount} 行 × {colCount} 列</span>
      </div>
      <div className="sheet-scroll">
        <table
          ref={ref}
          className="sheet-table"
          onMouseUp={(e) => {
            const cell = e.target.closest("td");
            if (!cell) return;
            onSelectCell({ r: Number(cell.dataset.r), c: Number(cell.dataset.c) });
          }}
        >
          <thead>
            <tr>
              <th className="corner-cell" />
              {Array.from({ length: colCount }, (_, c) => (
                <th key={c} className="col-head">{String.fromCharCode(65 + (c % 26))}{c >= 26 ? Math.floor(c / 26) + 1 : ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.max(rowCount, 1) }, (_, r) => (
              <tr key={r} className={selectedCell?.r === r ? "row-selected" : ""}>
                <th className="row-head">{r + 1}</th>
                {Array.from({ length: colCount }, (_, c) => {
                  const value = sheet.data[r]?.[c] ?? "";
                  const isSel = selectedCell?.r === r && selectedCell?.c === c;
                  return (
                    <td
                      key={c}
                      data-r={r}
                      data-c={c}
                      className={isSel ? "cell-selected" : ""}
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateCell(r, c, e.currentTarget.textContent || "")}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default XlsxEditor;
